import { io, type Socket } from "socket.io-client";
import { buildActiveRoleAuth, buildActiveRoleSocketOptions } from "@/api/active-role-header";
import type { AppNotification } from "@/components/Header/notifications/types";
import { EnvVariables } from "@/env-variables";
import LocalStorage from "@/storages/LocalStorage";
import { useAdditionalInfo } from "@/stores/domains/additionalInfo/useAdditionalInfo";

export const RAD_NOTIFICATIONS_NAMESPACE = "/rad-notifications";
export const RAD_ORDER_NOTIFIED_EVENT = "order:notified";

type EventCallback = (data: unknown) => void;

/**
 * Socket.IO client for clinic-scoped RAD notifications.
 * Namespace: /rad-notifications
 * Auth: { token: cognitoAccessToken, clinic: clinicUuid }
 * Server → client: order:notified (RadNotification)
 */
class RadNotificationsSocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private isConnecting = false;
  private listeners: Map<string, EventCallback[]> = new Map();
  private currentClinicId: string | null = null;

  public connect(clinicId: string): void {
    if (!clinicId) return;

    if (
      this.socket &&
      this.currentClinicId === clinicId &&
      (this.isConnected || this.isConnecting)
    ) {
      return;
    }

    if (this.socket) {
      this.disconnect();
    }

    const websocketUrl = EnvVariables.notificationWebsocketUrl || EnvVariables.websocketUrl;
    if (!websocketUrl) {
      console.warn(
        "Notification WebSocket URL not configured. RAD notifications socket will not be established.",
      );
      return;
    }

    const accessToken = LocalStorage.getAccessToken();
    if (!accessToken) {
      console.warn("No access token. RAD notifications socket will not be established.");
      return;
    }

    this.isConnecting = true;
    this.currentClinicId = clinicId;

    try {
      // `/rad-notifications` is a Socket.IO namespace (append to URL; path stays /socket.io).
      this.socket = io(`${websocketUrl}${RAD_NOTIFICATIONS_NAMESPACE}`, {
        // `auth` carries the active role as well; `extraHeaders` is sent for parity with HTTP
        // but only reaches a polling transport, which this namespace does not use.
        ...buildActiveRoleSocketOptions(
          { token: accessToken, clinic: clinicId },
          useAdditionalInfo.getState().role,
        ),
        transports: ["websocket"],
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 20000,
        withCredentials: false,
        autoConnect: true,
      });

      this.setupSocketListeners();
    } catch (error) {
      console.error("RadNotificationsSocketService - Error creating Socket.IO instance:", error);
      this.isConnecting = false;
      this.emit("error", { error: `Failed to create Socket.IO instance: ${error}` });
    }
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on("connect", () => {
      this.isConnected = true;
      this.isConnecting = false;
      this.emit("connect", {});
    });

    this.socket.on("disconnect", (reason) => {
      this.isConnected = false;
      this.isConnecting = false;
      this.emit("disconnect", { reason });
    });

    this.socket.on("connect_error", (error) => {
      console.error("RAD notifications socket connection error:", error.message);
      this.isConnecting = false;
      this.emit("error", { error: error.message });
    });

    this.socket.on(RAD_ORDER_NOTIFIED_EVENT, (payload: AppNotification) => {
      this.emit(RAD_ORDER_NOTIFIED_EVENT, payload);
    });
  }

  /**
   * Refresh auth token / clinic on the live socket (e.g. after silent token refresh).
   * Reconnects when clinic changes.
   */
  public refreshAuth(clinicId: string): void {
    const accessToken = LocalStorage.getAccessToken();
    if (!accessToken || !clinicId) return;

    if (this.currentClinicId !== clinicId) {
      this.connect(clinicId);
      return;
    }

    if (this.socket) {
      this.socket.auth = buildActiveRoleAuth(
        { token: accessToken, clinic: clinicId },
        useAdditionalInfo.getState().role,
      );
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;
    this.isConnecting = false;
    this.currentClinicId = null;
  }

  public on(event: string, callback: EventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(callback);
  }

  public off(event: string, callback: EventCallback): void {
    const callbacks = this.listeners.get(event);
    if (!callbacks) return;

    const index = callbacks.indexOf(callback);
    if (index !== -1) {
      callbacks.splice(index, 1);
    }
    if (callbacks.length === 0) {
      this.listeners.delete(event);
    }
  }

  private emit(event: string, data: unknown): void {
    const callbacks = this.listeners.get(event) || [];
    callbacks.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in RAD notifications "${event}" handler:`, error);
      }
    });
  }

  public isSocketConnected(): boolean {
    return this.isConnected && this.socket !== null;
  }

  public getCurrentClinicId(): string | null {
    return this.currentClinicId;
  }
}

const radNotificationsSocketService = new RadNotificationsSocketService();
export default radNotificationsSocketService;
